/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { cloneDataOrNull, DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { fromEnumAsStringOrNull } from '../../serialization/enum-utils.js';
import { PaymentStatus } from './payment-status.js';
import { PaymentProvider } from './payment-provider.js';
import { VersionRange } from '../core/version-range.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { PaymentProtocolSettings } from './payment-protocol-settings.js';
import { PaymentProtocol } from './payment-protocol.js';
import { PaymentType } from './payment-type.js';
import { InvoiceType } from './invoicing/invoice-type.js';
import { PaymentAccountRef } from './payment-account-ref.js';

export class PaymentIntent extends DataModel {
  static readonly TYPE_ID: string = '11fc5026ca4b4dae81bc4f58a8c01545';
  static readonly SCHEMA_VERSION: number = 4;

  private constructor(
    public readonly uniqueId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly order: CustomerOrderBase,
    public readonly paymentSettings: PaymentProtocolSettings | null,
    public readonly overrideCheckoutReturnUrl: string | null,
  ) {
    super();
  }

  get $typeId() {
    return PaymentIntent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(PaymentIntent.SCHEMA_VERSION);
  }

  get orderId(): string {
    return this.order.uniqueId;
  }

  get creatorId(): string {
    return this.order.creatorId;
  }

  get status(): PaymentStatus | null {
    return this.order.onlinePaymentDetails!.status;
  }

  get provider(): PaymentProvider | null {
    return this.order.onlinePaymentDetails!.provider;
  }

  get paymentRequestId(): string | null {
    return this.order.onlinePaymentDetails!.paymentRequestId;
  }

  get paymentId(): string | null {
    return this.order.onlinePaymentDetails!.paymentId;
  }

  get checkoutSessionId(): string | null {
    return this.order.onlinePaymentDetails!.checkoutSessionId;
  }

  get isGuestPurchase(): boolean {
    return this.order.isGuestOrder;
  }

  static ofOrder(
      order: CustomerOrderBase,
      paymentSettings: PaymentProtocolSettings,
      overrideCheckoutReturnUrl: string | null,
  ): PaymentIntent {
    if(isNullOrUndefined(order.onlinePaymentDetails) ||
        isNullOrUndefined(order.onlinePaymentDetails.paymentRequestId)) {
      throw new Error('The order does not have online payment details.');
    }

    return new PaymentIntent(
        order.onlinePaymentDetails.paymentRequestId,
        TimeZone.default().now(),
        order,
        cloneDataOrNull(paymentSettings),
        overrideCheckoutReturnUrl,
    );
  }

  static ofUpdatedOrder(oldPaymentIntent: PaymentIntent, order: CustomerOrderBase): PaymentIntent {
    if(isNullOrUndefined(order.onlinePaymentDetails) ||
        isNullOrUndefined(order.onlinePaymentDetails.paymentRequestId) ||
        order.onlinePaymentDetails.paymentRequestId != oldPaymentIntent.uniqueId) {
      throw new Error('The order does not have online payment details.');
    }

    return new PaymentIntent(
        oldPaymentIntent.uniqueId,
        TimeZone.default().now(),
        order,
        cloneDataOrNull(oldPaymentIntent.paymentSettings),
        oldPaymentIntent.overrideCheckoutReturnUrl,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PaymentIntent {
    return new PaymentIntent(
        MapUtils.getString(map, 'uniqueId'),
        context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
        context.fromData<CustomerOrderBase>(MapUtils.getRecord(map, 'order')),
        context.fromDataOrNull<PaymentProtocolSettings>(MapUtils.getRecordOrNull(map, 'paymentSettings')),
        MapUtils.getStringOrNull(map, 'overrideCheckoutReturnUrl'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'order': context.toData(this.order),
      'orderId': this.order.uniqueId,
      'creatorId': this.order.creatorId,
      'status': fromEnumAsStringOrNull(this.order.onlinePaymentDetails!.status),
      'provider': fromEnumAsStringOrNull(this.order.onlinePaymentDetails!.provider),
      'paymentRequestId': this.order.onlinePaymentDetails!.paymentRequestId,
      'paymentId': this.order.onlinePaymentDetails!.paymentId,
      'checkoutSessionId': this.order.onlinePaymentDetails!.checkoutSessionId,
      'creationDate': context.encodeDate(this.creationDate),
      'paymentSettings': context.toDataOrNull(this.paymentSettings),
      'isGuestPurchase': this.order.isGuestOrder,
      'overrideCheckoutReturnUrl': this.overrideCheckoutReturnUrl,
    };
  }

  clone(): this {
    return new PaymentIntent(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.order.clone(),
        cloneDataOrNull(this.paymentSettings),
        this.overrideCheckoutReturnUrl,
    ) as this;
  }

  isSame(other: PaymentIntent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.order, this.order) &&
      isSameData(other.paymentSettings, this.paymentSettings) &&
      this.overrideCheckoutReturnUrl === other.overrideCheckoutReturnUrl &&
      this.uniqueId === other.uniqueId &&
      this.creationDate === other.creationDate;
  }
}

export class PaymentIntentBuilder extends DataModelBuilder<PaymentIntent> {
  build(context: SerializationContext, map: SerializedData): PaymentIntent {
    return PaymentIntent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(PaymentIntent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(PaymentIntent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [paymentAccount] field was added.
    if(version < 2) version = 2;

    // In this version the [overrideCheckoutReturnUrl] field was added.
    if(version < 3) version = 3;

    if(version < 4) {
      const paymentAccount = context.fromDataOrNull<PaymentAccountRef>(MapUtils.getRecordOrNull(map, 'paymentAccount'));
      const order = context.fromData<CustomerOrderBase>(MapUtils.getRecord(map, 'order'));

      const paymentSettings = paymentAccount == null ? null : PaymentProtocolSettings.create({
        standard: PaymentProtocol.create({
          paymentAccount,
          paymentMethods: [PaymentType.Online],
          allowOfflineOrderReceipts: false,
          allowLegalEntityInvoices: order.onlinePaymentDetails?.invoiceType === InvoiceType.LegalEntity,
        }),
        products: null,
        donations: null,
      });

      map['paymentSettings'] = context.toDataOrNull(paymentSettings);
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
